/**
 * Kartica »Ujemanje otvoritve« na strani Uvoz (docs/ARHITEKTURA.md, 5.5).
 *
 * Otvoritev leta proti stanju 31. 12. preteklega leta: razlike po skupinah
 * izkaza so opozorilo, razlike po kontih samo informacija (preknjizbe ob
 * prehodu med programi). Skrbnik izbere leto; prikaz je zato za vsako leto
 * svoja poizvedba, ne vse naenkrat.
 */
import type { UjemanjeOtvoritve as Ujemanje } from '@koda/shared';
import { useEffect, useState, type JSX } from 'react';

import { api } from '../lib/api';
import { znesekZValuto } from '../lib/format';

interface Lastnosti {
  podjetje: string;
  /** Leta s knjizbami, najnovejse najprej; preverja se vsako, ki ima leto pred seboj. */
  leta: readonly number[];
  /** Spremeni se po uvozu, da se izid prebere znova. */
  osvezitev: string;
}

export function UjemanjeOtvoritve({ podjetje, leta, osvezitev }: Lastnosti): JSX.Element | null {
  const kandidati = leta.filter((l) => leta.includes(l - 1));
  const [leto, setLeto] = useState<number | null>(kandidati[0] ?? null);
  // Leta se lahko spremenijo po uvozu (dodano vmesno leto, izbrisano leto):
  // izbira ostane, dokler je veljavna, sicer se vzame prvo mozno.
  const kljucKandidatov = kandidati.join(',');
  useEffect(() => {
    const mozna = kljucKandidatov === '' ? [] : kljucKandidatov.split(',').map(Number);
    setLeto((trenutno) => (trenutno !== null && mozna.includes(trenutno) ? trenutno : (mozna[0] ?? null)));
  }, [kljucKandidatov]);
  const [izid, setIzid] = useState<Ujemanje | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [vsiKonti, setVsiKonti] = useState(false);

  useEffect(() => {
    if (leto === null) return;
    let veljaven = true;
    setIzid(null);
    setNapaka(null);
    api
      .ujemanjeOtvoritve(podjetje, leto)
      .then((u) => {
        if (veljaven) setIzid(u);
      })
      .catch((e: unknown) => {
        if (veljaven) setNapaka(e instanceof Error ? e.message : 'Ujemanja ni bilo mogoče preveriti.');
      });
    return () => {
      veljaven = false;
    };
  }, [podjetje, leto, osvezitev]);

  if (kandidati.length === 0 || leto === null) return null;

  return (
    <section className="kartica" aria-labelledby="ujemanje-otvoritve">
      <div className="glava-kartice">
        <h2 id="ujemanje-otvoritve">Ujemanje otvoritve</h2>
        <label className="drobno">
          Leto{' '}
          <select
            aria-label="Leto otvoritve"
            value={leto}
            onChange={(d) => {
              setLeto(Number.parseInt(d.target.value, 10));
            }}
          >
            {kandidati.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="podnaslov">
        Otvoritev leta proti stanju 31. 12. preteklega leta. Razlika po kontu je pogosto le preknjižba
        ob prehodu med programi; opozorilo nastane šele, kadar se ne ujema vsota skupine izkaza.
      </p>
      {napaka !== null && <p className="napaka">{napaka}</p>}
      {izid !== null && !izid.preverjeno && <p className="drobno">{izid.razlog}</p>}
      {izid !== null && izid.preverjeno && (
        <>
          {izid.ujema ? (
            <p>
              <span className="znacka-vira">ujema se</span>{' '}
              <span className="drobno">
                Otvoritev leta {izid.leto} se po skupinah ujema s stanjem 31. 12. {izid.izLeta}
                {izid.razlikeKontov.length > 0
                  ? `; razlike so samo med konti znotraj skupin (${String(izid.razlikeKontov.length)}).`
                  : '.'}
              </span>
            </p>
          ) : (
            <div className="opozorilo" role="alert">
              Otvoritev leta {izid.leto} se ne ujema s stanjem 31. 12. {izid.izLeta}:{' '}
              {izid.razlikeSkupin.map((s) => `${s.skupina} ${znesekZValuto(s.razlika)}`).join(', ')}. Preveri uvoza
              obeh let. Pri Business Centralu je pogost vzrok razlike v kapitalu in davku izvoz glavne
              knjige leta {izid.izLeta} brez knjižb z zaključnim datumom: izvozi jo z datumskim filtrom do{' '}
              <code>Z31.12.{String(izid.izLeta)}</code>, jo naloži znova in zaključni vrsti temeljnice (šifra
              izvora, npr. <code>ZAPUSPEHA</code>) nastavi vlogo zapiranje leta (izkaz).
            </div>
          )}
          {izid.kontoIzida === null && (
            <p className="drobno">
              Konto za prenos izida ni nastavljen; lanski izid je prikazan kot posebna vrstica.
            </p>
          )}
          {izid.razlikeKontov.length > 0 && (
            <>
              <button
                type="button"
                className="gumb-besedilo"
                onClick={() => {
                  setVsiKonti((v) => !v);
                }}
              >
                {vsiKonti ? 'Skrij razlike po kontih' : `Pokaži razlike po kontih (${String(izid.razlikeKontov.length)})`}
              </button>
              {vsiKonti && (
                <div className="tabela-ovoj">
                  <table>
                    <thead>
                      <tr>
                        <th>konto</th>
                        <th>naziv</th>
                        <th>skupina</th>
                        <th className="stevilka">stanje 31. 12. {izid.izLeta}</th>
                        <th className="stevilka">otvoritev {izid.leto}</th>
                        <th className="stevilka">razlika</th>
                      </tr>
                    </thead>
                    <tbody>
                      {izid.razlikeKontov.map((k) => (
                        <tr key={k.konto || 'izid'}>
                          <td>{k.konto || '—'}</td>
                          <td>{k.naziv ?? ''}</td>
                          <td>{k.skupina}</td>
                          <td className="stevilka">{znesekZValuto(k.lani)}</td>
                          <td className="stevilka">{znesekZValuto(k.otvoritev)}</td>
                          <td className="stevilka">{znesekZValuto(k.razlika)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}
